// Lib
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, type AvatarProps, type AvatarSize } from '@milanote/roma';

// Utils
import { getEmail, getGivenName, getId } from '../../../common/users/userHelper';
import { getIsAnonymousProp, getIsGuestProp, getUserImage } from '../../../common/users/utils/userPropertyUtils';
import { objectIdCreationSeconds } from '../../../common/utils/objectIdUtil';
import { getImageSource, getResponsiveImageSize } from '../../element/image/imageHelper';
import { getInitials } from '../../user/userUtil';

// Services
import { fetchUsers } from '../../user/userService';

// Constants
import { IMAGE_TYPES } from '../../../common/media/mediaConstants';

// Types
import type { ImMNUser, MNUser } from '../../../common/users/userModelTypes';
import type { ImMNCurrentUser } from '../../user/currentUserSelector';

const AVATAR_COLOURS = ['teal', 'blue', 'orange', 'red', 'yellow', 'purple', 'green', 'pink'];

const getAvatarColour = (user: MNUser | ImMNUser | ImMNCurrentUser): string | undefined =>
    AVATAR_COLOURS[objectIdCreationSeconds(getId(user)) % AVATAR_COLOURS.length];

/** Selects the persisted avatar variant that suits the rendered avatar size. */
const getAvatarImageSource = (imageDetails: unknown, size: AvatarSize): string | undefined => {
    const sizePx = Avatar.SIZES[size];
    const imageSize = getResponsiveImageSize({
        widthPx: sizePx,
        heightPx: sizePx,
        imageDetails,
        imageType: IMAGE_TYPES.AVATAR,
    });

    return (
        getImageSource({
            imageDetails,
            imageSize,
            elementId: undefined,
            useSecureMediaUrl: false,
            renderSecureImagesThroughCF: false,
        }) || undefined
    );
};

type UserAvatarUser = MNUser | ImMNUser | ImMNCurrentUser | undefined;

type UserAvatarIdentityProps = Pick<AvatarProps, 'src' | 'initials' | 'color'>;

/**
 * Derives Roma identity props from a Milanote user.
 */
export const getUserAvatarIdentity = (user: UserAvatarUser, size: AvatarSize): UserAvatarIdentityProps => {
    if (!user || getIsGuestProp(user) || getIsAnonymousProp(user)) return {};

    const imageDetails = getUserImage(user);
    const identityProps = { initials: getInitials(user), color: getAvatarColour(user) };

    if (imageDetails) return { ...identityProps, src: getAvatarImageSource(imageDetails, size) };
    if (getEmail(user) && !getGivenName(user)) return {};

    return identityProps;
};

type UserAvatarV2Props = {
    user: UserAvatarUser;
    size: AvatarSize;
    className?: string;
    status?: AvatarProps['status'];
    shouldFetchUserIfNotHydrated?: boolean;
};

/** Renders a Milanote user with the Roma avatar, keeping Roma free of user models and image infrastructure. */
export const UserAvatarV2: React.FC<UserAvatarV2Props> = ({
    user,
    size,
    className,
    status,
    shouldFetchUserIfNotHydrated = true,
}) => {
    const dispatch = useDispatch();
    const { src, initials, color } = getUserAvatarIdentity(user, size);
    const userId = getId(user);

    // User records can be ID-only; fetchUsers safely skips users that are already hydrated.
    useEffect(() => {
        if (!shouldFetchUserIfNotHydrated || !userId) return;

        dispatch(fetchUsers([userId]));
    }, [dispatch, shouldFetchUserIfNotHydrated, userId]);

    // Alt is blank because avatars always render alongside the user's visible name.
    return (
        <Avatar src={src} initials={initials} color={color} size={size} alt="" className={className} status={status} />
    );
};
